import { useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { HelpCircle, Search, PlayCircle } from "lucide-react";

// Central de Ajuda inicial (Pacote 2, item 11) — conteúdo estático,
// organizado por módulo, com busca no cliente e vídeo do YouTube opcional
// por artigo. Estrutura evolutiva: cada item já tem o formato (título,
// descrição, categoria, url de vídeo) que uma futura tela administrável de
// publicação poderia alimentar sem mudar esta página — decisão deliberada
// de não construir esse CMS agora, para não ampliar o escopo do pacote.
const ARTICLES = [
  { categoria: "Dashboard", titulo: "Como ler os indicadores do Dashboard", descricao: "O Dashboard mostra um resumo da competência selecionada: serviços, horas, faturamento e boletos. Use o seletor de competência para ver meses anteriores e o filtro de cliente para ver os números de um cliente específico.", video: "" },
  { categoria: "Clientes", titulo: "Cadastrando um cliente", descricao: "Em Clientes, use \"Novo cliente\" para cadastrar razão social, dados de contato e o valor-hora padrão, usado automaticamente nas fichas desse cliente quando nenhum valor específico é informado.", video: "" },
  { categoria: "Fichas", titulo: "Criando uma Ficha de Serviço", descricao: "Em Fichas de Serviço, informe cliente, analista, produto/sistema, horários de início e término e a descrição do atendimento. Ao encerrar a ficha, ela segue para aprovação (se exigida) e depois para assinatura do cliente.", video: "" },
  { categoria: "Fichas", titulo: "Produto/Sistema organizado por Grupo", descricao: "O campo Produto/Sistema é selecionado a partir do catálogo cadastrado em Configurações, organizado por Grupo. Fichas antigas com um valor digitado livremente continuam sendo exibidas normalmente.", video: "" },
  { categoria: "Aprovações", titulo: "Aprovando ou rejeitando uma ficha", descricao: "Fichas encerradas por analistas com aprovação exigida aparecem em Aprovações. Um supervisor com Grupos atribuídos só vê e atua nas fichas dos Produtos/Sistemas dos seus Grupos.", video: "" },
  { categoria: "Faturamento", titulo: "Gerando um faturamento", descricao: "Em Faturamento & Cobrança, escolha \"Todos os clientes\" para ver as fichas elegíveis de todo o seu tenant, ou um cliente específico. A geração de uma fatura sempre reúne fichas de um único cliente.", video: "" },
  { categoria: "Relatórios", titulo: "Exportando relatórios", descricao: "Os relatórios podem ser filtrados por cliente, analista e período, e exportados em planilha para análise externa.", video: "" },
  { categoria: "Configurações", titulo: "Personalizando a identidade visual", descricao: "Em Configurações → Personalização, defina o Nome Fantasia exibido, a logo e as cores principal, de destaque e de apoio. A pré-visualização mostra o resultado antes de salvar.", video: "" },
  { categoria: "Configurações", titulo: "Grupos e Produtos/Sistemas", descricao: "Cadastre os Grupos da sua empresa e vincule cada Produto/Sistema a um Grupo. Supervisores podem ser autorizados para um ou mais Grupos no cadastro de usuários.", video: "" },
];

const CATEGORIAS = [...new Set(ARTICLES.map(a => a.categoria))];

// Extrai o ID de um link do YouTube de forma estrita (watch?v=, youtu.be/,
// /embed/) — nunca aceita uma URL arbitrária como src do iframe.
const YOUTUBE_RE = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/i;
function youtubeEmbedUrl(url) {
  const m = YOUTUBE_RE.exec((url || "").trim());
  return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : null;
}

export default function Ajuda() {
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return ARTICLES.filter(a => {
      if (categoria && a.categoria !== categoria) return false;
      if (term && !`${a.titulo} ${a.descricao}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [q, categoria]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><HelpCircle size={28} className="text-primary" /> Central de Ajuda</h1>
        <p className="text-slate-500 text-sm">Tutoriais e explicações organizados por módulo do LOOTUS</p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Pesquisar na Ajuda..." value={q} onChange={e => setQ(e.target.value)} data-testid="ajuda-busca" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge onClick={() => setCategoria("")} className={`cursor-pointer ${categoria === "" ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700"}`}>Todas</Badge>
            {CATEGORIAS.map(c => (
              <Badge key={c} onClick={() => setCategoria(c)} className={`cursor-pointer ${categoria === c ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700"}`}>{c}</Badge>
            ))}
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {filtered.map((a, i) => {
          const embed = youtubeEmbedUrl(a.video);
          return (
            <Card key={i} className="p-5" data-testid={`ajuda-artigo-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-slate-100 text-slate-700">{a.categoria}</Badge>
                <span className="font-heading font-semibold text-slate-900">{a.titulo}</span>
              </div>
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.descricao}</p>
              {embed && (
                <div className="mt-3 aspect-video max-w-xl rounded-md overflow-hidden border border-slate-200">
                  <iframe
                    className="w-full h-full"
                    src={embed}
                    title={a.titulo}
                    allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    sandbox="allow-scripts allow-same-origin allow-presentation"
                    allowFullScreen
                  />
                </div>
              )}
              {a.video && !embed && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-1.5">
                  <PlayCircle size={14} /> Link de vídeo inválido — só vídeos do YouTube são incorporados.
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="p-8 text-center text-slate-400">Nenhum artigo encontrado com os filtros atuais.</Card>
        )}
      </div>
    </div>
  );
}
